import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import VideocamIcon from '@mui/icons-material/Videocam';
import HeroSalaryChart from './HeroSalaryChart';
import HeroSwitchCard from './HeroSwitchCard';
import HeroHikeCard from './HeroHikeCard';
import { cohorts, formatDate } from '../../data/liveClasses';
import { interviewQuestionCount } from '../../data/learn';

const nextCohort = [...cohorts].sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

const rotatingPhrases = ['Land the job.', 'Get the offer.', 'Grow your salary.'];

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 90, behavior: 'smooth' });
};

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;


// Career stories the hero window rotates through, each with its own card design
const heroSlides = [HeroSalaryChart, HeroSwitchCard, HeroHikeCard];
const SLIDE_MS = 3000;

// Rotating career cards with floating cards for interview prep and the next live cohort
const HeroVisual = () => {
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;
    const timer = setInterval(() => setSlide((i) => (i + 1) % heroSlides.length), SLIDE_MS);
    return () => clearInterval(timer);
  }, [paused]);

  return (
    <div
      className="hero-visual"
      aria-hidden="true"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="hero-window">
        {/* All slides share one grid cell so the window keeps the tallest height;
            the active one is re-keyed so its entrance animations replay. */}
        <div className="hero-slides">
          {heroSlides.map((Slide, i) => (
            <div
              key={i === slide ? `active-${i}` : `idle-${i}`}
              className={`hero-slide ${i === slide ? 'is-active' : ''}`}
            >
              <Slide />
            </div>
          ))}
        </div>
        <span className="hero-slide-dots">
          {heroSlides.map((_, i) => <i key={i} className={i === slide ? 'active' : ''} />)}
        </span>
      </div>

      <div className="hero-float hero-float--qa">
        <QuestionAnswerOutlinedIcon sx={{ fontSize: 18 }} />
        <div>
          <strong>Interview prep</strong>
          <span>{interviewQuestionCount}+ real questions with answers</span>
        </div>
      </div>

      <div className="hero-float hero-float--live">
        <span className="live-dot" />
        <VideocamIcon sx={{ fontSize: 18 }} />
        <div>
          <strong>{nextCohort.course.title}</strong>
          <span>Live cohort · starts {formatDate(nextCohort.startDate)}</span>
        </div>
      </div>
    </div>
  );
};

const RotatingPhrase = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % rotatingPhrases.length), 2800);
    return () => clearInterval(timer);
  }, []);

  return (
    <span className="hero-rotator">
      <span className="text-gradient hero-rotator-word" key={index}>{rotatingPhrases[index]}</span>
    </span>
  );
};

const HomeHero = () => {
  const heroRef = useRef(null);
  const frame = useRef(0);

  // Feed the pointer position to CSS: drives the spotlight, card tilt and parallax.
  const handlePointerMove = (e) => {
    if (e.pointerType !== 'mouse') return;
    const el = heroRef.current;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      el.style.setProperty('--sx', `${x * 100}%`);
      el.style.setProperty('--sy', `${y * 100}%`);
      el.style.setProperty('--px', (x - 0.5).toFixed(3));
      el.style.setProperty('--py', (y - 0.5).toFixed(3));
    });
  };

  const handlePointerLeave = () => {
    const el = heroRef.current;
    el.style.setProperty('--px', 0);
    el.style.setProperty('--py', 0);
  };

  return (
    <section
      className="home-hero"
      id="home"
      ref={heroRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <div className="home-hero-bg" aria-hidden="true">
        <span className="aurora aurora--1" />
        <span className="aurora aurora--2" />
        <span className="aurora aurora--3" />
        <span className="hero-spotlight" />
      </div>
      <div className="container home-hero-inner">
        <div className="home-hero-copy">
          <Link to="/live-classes" className="hero-announce hero-in" style={{ '--d': '0ms' }}>
            <span className="hero-announce-tag">New</span>
            Agentic AI live cohort is now open
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Link>

          <h1>
            <span className="hero-line hero-in" style={{ '--d': '120ms' }}>Learn the skills.</span>
            <span className="hero-line hero-in" style={{ '--d': '240ms' }}>Crack the interview.</span>
            <span className="hero-line hero-in" style={{ '--d': '360ms' }}><RotatingPhrase /></span>
          </h1>

          <p className="home-hero-sub hero-in" style={{ '--d': '480ms' }}>
            Full-Stack, Java, DSA, System Design and Agentic AI, taught by engineers who have built
            products used by millions. Free lessons to start, live cohorts to go all the way.
          </p>

          <div className="home-hero-ctas hero-in" style={{ '--d': '600ms' }}>
            <Link to="/learn" className="btn-hero-primary">
              Start learning free <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Link>
            <a href="#interview-prep" className="btn-hero-secondary" onClick={scrollTo('interview-prep')}>
              <PlayCircleOutlineIcon sx={{ fontSize: 20 }} /> Prepare for interviews
            </a>
          </div>

          <ul className="home-hero-points hero-in" style={{ '--d': '720ms' }}>
            <li><CheckCircleIcon sx={{ fontSize: 16 }} /> No sign-up needed for lessons</li>
            <li><CheckCircleIcon sx={{ fontSize: 16 }} /> Live, mentor-led cohorts</li>
            <li><CheckCircleIcon sx={{ fontSize: 16 }} /> Interview-focused</li>
          </ul>
        </div>

        <div className="hero-in hero-in--visual" style={{ '--d': '300ms' }}>
          <HeroVisual />
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
